import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Tour guiado leve, sem dependência externa.
 *
 * Por que não react-joyride/shepherd: pacotes pesados (~50kb+) pra
 * algo que precisamos só em 1-2 fluxos. Componente próprio nos dá
 * controle total do estilo (PAKT design — cantos retos, paleta) +
 * zero deps novas.
 *
 * Como funciona:
 *   - Aceita lista de passos com targetSelector (CSS, ex: '[data-tour="X"]')
 *   - Pra cada passo: overlay escuro + "buraco" sobre o target (mask CSS)
 *     + tooltip ao lado com título/body/CTA
 *   - Persiste progresso em localStorage[storageKey]
 *   - Avança via "Próximo" OU automaticamente se o user clicou no target
 *     (mode='auto' com waitForNavigation)
 *   - Pula tudo via "Pular tour" (marca completed pra nunca mais voltar)
 *
 * Resilience:
 *   - Se target não encontrado em 1s → pula o passo (silencioso)
 *   - resize/scroll → reposiciona automaticamente
 *   - Esc → fecha (não marca completed)
 */

export interface TourStep {
  /** seletor CSS do elemento alvo (ex: '[data-tour="new-doc"]') */
  targetSelector: string
  title: string
  body: string
  /** posição do tooltip relativo ao target */
  placement?: 'top' | 'bottom' | 'left' | 'right'
  /** texto do botão "próximo" do passo (default "Próximo") */
  nextLabel?: string
  /**
   * Quando true, espera o usuário clicar no target em vez de mostrar
   * "Próximo". Útil quando o próximo passo só faz sentido após a ação.
   */
  waitForClick?: boolean
}

interface TourProps {
  steps: TourStep[]
  storageKey: string
  /** quando true (default), só mostra se nunca completou */
  oncePerStorageKey?: boolean
  /** chamado quando user termina ou pula */
  onComplete?: () => void
}

interface Position {
  top: number
  left: number
  width: number
  height: number
}

export function Tour({
  steps,
  storageKey,
  oncePerStorageKey = true,
  onComplete,
}: TourProps) {
  const [stepIdx, setStepIdx] = useState(0)
  const [position, setPosition] = useState<Position | null>(null)
  const [visible, setVisible] = useState(false)
  const attemptsRef = useRef(0)

  // Decide se deve mostrar (primeira vez OU oncePerStorageKey=false)
  useEffect(() => {
    if (!oncePerStorageKey) { setVisible(true); return }
    const completed = localStorage.getItem(storageKey)
    if (!completed) setVisible(true)
  }, [storageKey, oncePerStorageKey])

  const currentStep = steps[stepIdx]

  // Localiza o target + atualiza posição. Re-tenta por 1s se não achar
  // (target pode estar montando após data fetch).
  useLayoutEffect(() => {
    if (!visible || !currentStep) return

    attemptsRef.current = 0

    function updatePosition() {
      const el = document.querySelector<HTMLElement>(currentStep.targetSelector)
      if (!el) {
        attemptsRef.current++
        if (attemptsRef.current > 10) {
          // Não achou após 1s — pula o passo silencioso
          console.warn(`[Tour] target não encontrado: ${currentStep.targetSelector}`)
          handleNext()
          return
        }
        setTimeout(updatePosition, 100)
        return
      }
      const r = el.getBoundingClientRect()
      setPosition({ top: r.top, left: r.left, width: r.width, height: r.height })

      // Auto-scroll pra elemento ficar visível
      if (r.top < 80 || r.top > window.innerHeight - 200) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
    updatePosition()

    function onResize() {
      const el = document.querySelector<HTMLElement>(currentStep.targetSelector)
      if (el) {
        const r = el.getBoundingClientRect()
        setPosition({ top: r.top, left: r.left, width: r.width, height: r.height })
      }
    }
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onResize, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onResize, true)
    }
  }, [visible, stepIdx, currentStep?.targetSelector])

  // Se step tem waitForClick: avança quando user clicar no target.
  // Captura no fase de bubble pra não bloquear o clique real.
  useEffect(() => {
    if (!visible || !currentStep?.waitForClick) return
    const el = document.querySelector<HTMLElement>(currentStep.targetSelector)
    if (!el) return
    function onClick() { handleNext() }
    el.addEventListener('click', onClick, { once: true })
    return () => el.removeEventListener('click', onClick)
  }, [visible, stepIdx, currentStep])

  // Esc fecha sem marcar completed
  useEffect(() => {
    if (!visible) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setVisible(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [visible])

  function handleNext() {
    if (stepIdx + 1 >= steps.length) {
      finish(true)
    } else {
      setStepIdx(stepIdx + 1)
    }
  }

  function handleSkip() {
    finish(true)
  }

  function finish(markCompleted: boolean) {
    if (markCompleted) localStorage.setItem(storageKey, '1')
    setVisible(false)
    onComplete?.()
  }

  if (!visible || !currentStep || !position) return null

  // Tooltip placement
  const PAD = 12
  const tipStyle: React.CSSProperties = {}
  const placement = currentStep.placement ?? 'bottom'
  if (placement === 'bottom') {
    tipStyle.top = position.top + position.height + PAD
    tipStyle.left = Math.max(16, Math.min(position.left, window.innerWidth - 360))
  } else if (placement === 'top') {
    tipStyle.bottom = window.innerHeight - position.top + PAD
    tipStyle.left = Math.max(16, Math.min(position.left, window.innerWidth - 360))
  } else if (placement === 'right') {
    tipStyle.top = position.top
    tipStyle.left = position.left + position.width + PAD
  } else if (placement === 'left') {
    tipStyle.top = position.top
    tipStyle.right = window.innerWidth - position.left + PAD
  }

  return (
    <>
      {/* Overlay com 4 retângulos cercando o target (faz "buraco") */}
      <div
        className="fixed inset-0 z-[100] pointer-events-none"
        aria-hidden="true"
      >
        {/* topo */}
        <div
          className="absolute bg-black/60 pointer-events-auto"
          style={{
            top: 0, left: 0, right: 0,
            height: Math.max(0, position.top - 4),
          }}
          onClick={handleSkip}
        />
        {/* baixo */}
        <div
          className="absolute bg-black/60 pointer-events-auto"
          style={{
            top: position.top + position.height + 4,
            left: 0, right: 0, bottom: 0,
          }}
          onClick={handleSkip}
        />
        {/* esquerda */}
        <div
          className="absolute bg-black/60 pointer-events-auto"
          style={{
            top: Math.max(0, position.top - 4),
            left: 0,
            width: Math.max(0, position.left - 4),
            height: position.height + 8,
          }}
          onClick={handleSkip}
        />
        {/* direita */}
        <div
          className="absolute bg-black/60 pointer-events-auto"
          style={{
            top: Math.max(0, position.top - 4),
            left: position.left + position.width + 4,
            right: 0,
            height: position.height + 8,
          }}
          onClick={handleSkip}
        />
        {/* outline laranja em volta do target */}
        <div
          className="absolute border-2 border-pakt-orange pointer-events-none"
          style={{
            top: position.top - 4,
            left: position.left - 4,
            width: position.width + 8,
            height: position.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0)',
          }}
        />
      </div>

      {/* Tooltip */}
      <div
        className="fixed z-[101] max-w-sm bg-white border border-pakt-line shadow-2xl p-4"
        style={tipStyle}
        role="dialog"
        aria-labelledby="tour-title"
      >
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono uppercase tracking-wider text-[10px] text-pakt-orange">
            {stepIdx + 1} / {steps.length}
          </span>
          <button
            onClick={handleSkip}
            className="text-xs font-mono uppercase tracking-wider text-pakt-ink-3 hover:text-pakt-ink"
          >
            pular tour
          </button>
        </div>
        <h3 id="tour-title" className="font-semibold text-pakt-ink mb-1">{currentStep.title}</h3>
        <p className="text-sm text-pakt-ink-2 leading-relaxed">{currentStep.body}</p>
        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
            disabled={stepIdx === 0}
            className="text-xs font-mono uppercase tracking-wider text-pakt-ink-3 hover:text-pakt-ink disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ← voltar
          </button>
          {!currentStep.waitForClick && (
            <button
              onClick={handleNext}
              className="bg-pakt-ink hover:bg-pakt-ink-2 text-white px-4 py-2 text-xs uppercase tracking-wide"
            >
              {currentStep.nextLabel ?? (stepIdx + 1 >= steps.length ? 'Concluir' : 'Próximo')}
            </button>
          )}
          {currentStep.waitForClick && (
            <span className="text-xs font-mono uppercase tracking-wider text-pakt-orange animate-pulse">
              ↓ clique no destacado
            </span>
          )}
        </div>
      </div>
    </>
  )
}
